import React, { useEffect, useMemo, useState } from "react";
import { Button, Modal, Select, Tag, message } from "antd";
import {
  ClockCircleOutlined,
  LoginOutlined,
  LogoutOutlined,
  UserSwitchOutlined,
} from "@ant-design/icons";
import axios from "axios";
import dayjs from "dayjs";
import { BASE_API_URL } from "../../utils/consts";
import "./AssignOctopusShiftModal.scss";

interface InternalUser {
  _id: string;
  firstName?: string;
  lastName?: string;
  username?: string;
}

interface OctopusRecord {
  Id: number;
  UserFullName: string;
  CheckIn: string;
  CheckOut?: string;
  AlreadyAssigned?: boolean;
}

interface Props {
  record: OctopusRecord | null;
  users: InternalUser[];
  clientId: string;
  locationId?: string;
  onClose: () => void;
  onAssigned: (octopusId: number) => void;
}

const normalize = (s: string) =>
  (s || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .trim();

const userLabel = (u: InternalUser) =>
  `${u.firstName || ""} ${u.lastName || ""}`.trim() || u.username || "";

const initials = (name: string) =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0])
    .join("")
    .toUpperCase();

const AssignOctopusShiftModal: React.FC<Props> = ({
  record,
  users,
  clientId,
  locationId,
  onClose,
  onAssigned,
}) => {
  const [selectedUserId, setSelectedUserId] = useState<string | undefined>();
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!record) {
      setSelectedUserId(undefined);
      return;
    }
    // Best-effort: preselect if exactly one of our users matches the Octopus name.
    const q = normalize(record.UserFullName);
    const matches = users.filter((u) => normalize(userLabel(u)) === q);
    setSelectedUserId(matches.length === 1 ? matches[0]._id : undefined);
  }, [record, users]);

  const userOptions = useMemo(
    () => users.map((u) => ({ value: u._id, label: userLabel(u) })),
    [users]
  );

  const handleConfirm = async () => {
    if (!record || !selectedUserId || !record.CheckOut) return;
    setSaving(true);
    try {
      const payload: Record<string, any> = {
        userId: selectedUserId,
        clientId,
        date: dayjs(record.CheckIn).format("YYYY-MM-DD"),
        startTime: record.CheckIn,
        endTime: record.CheckOut,
        shiftType: "HOURS",
        octopusUserRegistrationId: record.Id,
      };
      if (locationId) payload.locationId = locationId;

      await axios.post(`${BASE_API_URL}/shifts/create`, payload);
      message.success("Smjena je dodana kod izabranog zaposlenog.");
      onAssigned(record.Id);
      onClose();
    } catch (err: any) {
      message.error(
        err?.response?.data?.message || "Greška pri dodavanju smjene."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={
        record && (
          <div className="aosm-header">
            <div className="aosm-avatar">{initials(record.UserFullName)}</div>
            <div>
              <div className="aosm-header-name">{record.UserFullName}</div>
              <div className="aosm-header-sub">Octopus smjena</div>
            </div>
          </div>
        )
      }
      open={!!record}
      onCancel={onClose}
      className="assign-octopus-shift-modal"
      wrapClassName="assign-octopus-shift-modal-wrap"
      footer={null}
      destroyOnClose
    >
      {record && (
        <>
          <div className="aosm-card">
            <div className="aosm-card-row">
              <span className="aosm-card-icon">
                <LoginOutlined />
              </span>
              <span className="aosm-card-label">Prijava</span>
              <span className="aosm-card-value">
                {dayjs(record.CheckIn).format("DD.MM.YYYY HH:mm")}
              </span>
            </div>
            <div className="aosm-card-row">
              <span className="aosm-card-icon">
                <LogoutOutlined />
              </span>
              <span className="aosm-card-label">Odjava</span>
              <span className="aosm-card-value">
                {record.CheckOut ? (
                  dayjs(record.CheckOut).format("DD.MM.YYYY HH:mm")
                ) : (
                  <Tag color="green">Još prijavljen</Tag>
                )}
              </span>
            </div>
            {record.CheckOut && (
              <div className="aosm-card-row">
                <span className="aosm-card-icon">
                  <ClockCircleOutlined />
                </span>
                <span className="aosm-card-label">Trajanje</span>
                <span className="aosm-card-value">
                  {(
                    dayjs(record.CheckOut).diff(dayjs(record.CheckIn), "minute") / 60
                  ).toFixed(1)}{" "}
                  h
                </span>
              </div>
            )}
          </div>

          <div className="aosm-field-label">Dodijeli zaposlenom</div>
          <Select
            className="aosm-select"
            placeholder="Izaberite zaposlenog"
            value={selectedUserId}
            onChange={setSelectedUserId}
            showSearch
            optionFilterProp="label"
            options={userOptions}
            disabled={!record.CheckOut}
            suffixIcon={<UserSwitchOutlined />}
          />

          <div className="aosm-hint">
            Smjena će biti dodana kao satnica (HOURS), sa vremenom prijave i
            odjave preuzetim iz Octopusa.
          </div>

          <div className="aosm-actions">
            <Button onClick={onClose}>Otkaži</Button>
            <Button
              type="primary"
              loading={saving}
              disabled={!selectedUserId || !record.CheckOut}
              onClick={handleConfirm}
            >
              Potvrdi i dodaj
            </Button>
          </div>
        </>
      )}
    </Modal>
  );
};

export default AssignOctopusShiftModal;
